import React from 'react';
import '../../styles/weeklyLearningGuideA4Card.css';
import type {
  WeeklyLearningGuideExportCell,
  WeeklyLearningGuideExportPayload,
  WeeklyLearningGuideExportWeekBlock,
} from '../../utils/weeklyLearningGuideExcelExport';

const TRACK_FALLBACK_HEX = '#c9ced6';

/** html2canvas는 color-mix를 못 그리므로 흰색과의 혼합색을 직접 계산한다 */
function mixHexWithWhite(hex: string | undefined, weight: number): string {
  const raw = (hex ?? '').trim().replace(/^#/, '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const n = /^[0-9a-f]{6}$/i.test(full) ? parseInt(full, 16) : parseInt(TRACK_FALLBACK_HEX.slice(1), 16);
  const mix = (channel: number) => Math.round(channel * weight + 255 * (1 - weight));
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
}

function WeeklyGuideA4Card({ cell }: { cell: WeeklyLearningGuideExportCell }) {
  if (cell.slotSurface === 'holiday') {
    return (
      <div className="cp-wlg-a4c-card cp-wlg-a4c-card--holiday">
        <span className="cp-wlg-a4c-surface-text">{cell.subject}</span>
      </div>
    );
  }
  if (cell.slotSurface !== 'class') {
    return (
      <div
        className={
          'cp-wlg-a4c-card ' +
          (cell.slotSurface === 'activity' ? 'cp-wlg-a4c-card--activity' : 'cp-wlg-a4c-card--off')
        }
      >
        <span className="cp-wlg-a4c-surface-text">{cell.subject}</span>
      </div>
    );
  }
  const title = (cell.subjectTitle ?? '').trim() || cell.subject.trim();
  if (!title && !cell.learning.trim() && !cell.unit.trim()) {
    return <div className="cp-wlg-a4c-card" />;
  }
  const track = cell.subjectBgHex;
  return (
    <div className="cp-wlg-a4c-card">
      <div
        className="cp-wlg-a4c-head"
        style={{
          backgroundImage: `linear-gradient(90deg, ${mixHexWithWhite(track, 0.26)}, ${mixHexWithWhite(track, 0.06)})`,
        }}
      >
        <div className="cp-wlg-a4c-head-row">
          <span
            className="cp-wlg-a4c-subject"
            style={{
              backgroundImage: `linear-gradient(transparent 58%, ${mixHexWithWhite(track, 0.6)} 58%)`,
            }}
          >
            {title}
          </span>
          {cell.subjectSubtitle ? <span className="cp-wlg-a4c-timetable">{cell.subjectSubtitle}</span> : null}
        </div>
        <div className="cp-wlg-a4c-page">{cell.page || '\u00a0'}</div>
      </div>
      <div className="cp-wlg-a4c-body">
        {cell.unit || cell.evalCode ? (
          <div className="cp-wlg-a4c-unit-row">
            <span className="cp-wlg-a4c-unit">{cell.unit}</span>
            {cell.evalCode ? (
              <span className={`cp-wlg-a4c-eval cp-wlg-a4c-eval--${cell.evalCode}`}>{cell.evalLabel}</span>
            ) : null}
          </div>
        ) : null}
        <div className="cp-wlg-a4c-learning">{cell.learning}</div>
      </div>
      <div className="cp-wlg-a4c-foot">{cell.material ? `🧪 ${cell.material}` : '\u00a0'}</div>
    </div>
  );
}

/** 축소가 이 비율보다 커져야 한 장에 들어가면 교시 단위로 다음 쪽에 넘긴다 */
const MIN_PDF_FIT_SCALE = 0.8;

function WeeklyGuideA4CardSheet({
  block,
  periodIndexes,
  showNotice,
  pageLabel,
  measuring = false,
  pageBreakAfter = false,
}: {
  block: WeeklyLearningGuideExportWeekBlock;
  periodIndexes: number[];
  showNotice: boolean;
  pageLabel?: string;
  measuring?: boolean;
  pageBreakAfter?: boolean;
}) {
  const { titleLine1, titleLine2, columns, periods, noticeText } = block;
  const hasNotice = showNotice && noticeText.trim().length > 0;
  return (
    <div
      className={
        'cp-weekly-learning-guide-a4-sheet cp-wlg-a4c-sheet' +
        (measuring ? ' cp-wlg-a4c-sheet--measuring' : '') +
        (pageBreakAfter ? ' cp-weekly-learning-guide-a4-sheet--page-break' : '')
      }
    >
      <header data-pdf-element="header" className="cp-wlg-a4c-title" data-wlg-measure="title">
        <p className="cp-wlg-a4c-title-line1">
          {titleLine1}
          {pageLabel ? <span className="cp-wlg-a4c-page-label"> {pageLabel}</span> : null}
        </p>
        {titleLine2.trim() ? <p className="cp-wlg-a4c-title-line2">{titleLine2}</p> : null}
      </header>
      <div
        className="cp-wlg-a4c-grid"
        style={{
          gridTemplateColumns: `11mm repeat(${columns.length}, minmax(0, 1fr))`,
          gridTemplateRows: `max-content repeat(${periodIndexes.length}, auto)${hasNotice ? ' max-content' : ''}`,
        }}
      >
        <div className="cp-wlg-a4c-corner" data-wlg-measure="dayhead">
          구분
        </div>
        {columns.map((column, idx) => (
          <div key={`head-${idx}`} className="cp-wlg-a4c-dayhead">
            <div className="cp-wlg-a4c-day">{column.weekdayLabel}</div>
            <div className="cp-wlg-a4c-date">{column.dateHeader}</div>
          </div>
        ))}
        {periodIndexes.map((pIdx) => {
          const period = periods[pIdx]!;
          return (
            <React.Fragment key={`period-${pIdx}`}>
              <div className="cp-wlg-a4c-period" data-wlg-period-row={pIdx}>
                {period.label}
              </div>
              {period.cells.map((cell, cIdx) => (
                <WeeklyGuideA4Card key={`cell-${pIdx}-${cIdx}`} cell={cell} />
              ))}
            </React.Fragment>
          );
        })}
        {hasNotice ? (
          <>
            <div className="cp-wlg-a4c-period" data-wlg-measure="notice">
              <span>
                가정
                <br />
                통신문
              </span>
            </div>
            <div className="cp-wlg-a4c-notice" style={{ gridColumn: `span ${columns.length}` }}>
              {noticeText}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** 측정한 행 높이로 교시를 쪽에 나눔(각 쪽은 축소 비율이 MIN_PDF_FIT_SCALE 이상이 되도록) */
function paginateMeasuredSheet(sheet: HTMLElement, periodCount: number): number[][] {
  const all = Array.from({ length: periodCount }, (_, i) => i);
  const a4Height = (sheet.offsetWidth * 297) / 210;
  const maxHeight = a4Height / MIN_PDF_FIT_SCALE;
  if (sheet.offsetHeight <= maxHeight || periodCount <= 1) return [all];

  const sheetStyle = getComputedStyle(sheet);
  const grid = sheet.querySelector<HTMLElement>('.cp-wlg-a4c-grid');
  const gridGap = grid ? parseFloat(getComputedStyle(grid).rowGap) || 0 : 0;
  const sheetGap = parseFloat(sheetStyle.rowGap) || 0;
  const heightOf = (selector: string) => sheet.querySelector<HTMLElement>(selector)?.offsetHeight ?? 0;
  const fixed =
    (parseFloat(sheetStyle.paddingTop) || 0) +
    (parseFloat(sheetStyle.paddingBottom) || 0) +
    heightOf('[data-wlg-measure="title"]') +
    sheetGap +
    heightOf('[data-wlg-measure="dayhead"]');
  const noticeHeight = heightOf('[data-wlg-measure="notice"]');
  const rowHeights = all.map((i) => heightOf(`[data-wlg-period-row="${i}"]`));

  const pages: number[][] = [];
  let current: number[] = [];
  let used = fixed;
  rowHeights.forEach((h, i) => {
    const add = gridGap + h;
    if (current.length > 0 && used + add > maxHeight) {
      pages.push(current);
      current = [];
      used = fixed;
    }
    current.push(i);
    used += add;
  });
  if (noticeHeight > 0 && current.length > 0 && used + gridGap + noticeHeight > maxHeight) {
    pages.push(current);
    current = [];
  }
  pages.push(current);
  return pages;
}

/** PDF 저장 전용: 화면의 블럭형(카드) 디자인을 A4에 맞춘 문서(넘치면 교시 단위로 쪽 나눔) */
const WeeklyLearningGuideA4CardDocument = React.forwardRef<
  HTMLDivElement,
  { payload: WeeklyLearningGuideExportPayload }
>(function WeeklyLearningGuideA4CardDocument({ payload }, ref) {
  const { titleLine1, titleLine2, columns, periods, noticeText, termWeekBlocks } = payload;
  const blocks: WeeklyLearningGuideExportWeekBlock[] = React.useMemo(
    () =>
      termWeekBlocks && termWeekBlocks.length > 0
        ? termWeekBlocks
        : [{ titleLine1, titleLine2, columns, periods, noticeText }],
    [columns, noticeText, periods, termWeekBlocks, titleLine1, titleLine2]
  );
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const setRootRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );
  /** null이면 측정 단계: 블럭마다 한 장으로 그려 높이를 잰다 */
  const [pagesByBlock, setPagesByBlock] = React.useState<number[][][] | null>(null);

  React.useLayoutEffect(() => {
    setPagesByBlock(null);
  }, [blocks]);

  React.useLayoutEffect(() => {
    if (pagesByBlock !== null) return;
    const root = rootRef.current;
    if (!root) return;
    const sheets = Array.from(root.querySelectorAll<HTMLElement>('.cp-wlg-a4c-sheet'));
    setPagesByBlock(
      blocks.map((block, index) => {
        const sheet = sheets[index];
        const all = block.periods.map((_, i) => i);
        return sheet ? paginateMeasuredSheet(sheet, block.periods.length) : [all];
      })
    );
  }, [blocks, pagesByBlock]);

  const entries: Array<{
    block: WeeklyLearningGuideExportWeekBlock;
    blockIndex: number;
    periodIndexes: number[];
    pageIndex: number;
    pageCount: number;
  }> = [];
  blocks.forEach((block, blockIndex) => {
    const pages = pagesByBlock?.[blockIndex] ?? [block.periods.map((_, i) => i)];
    pages.forEach((periodIndexes, pageIndex) => {
      entries.push({ block, blockIndex, periodIndexes, pageIndex, pageCount: pages.length });
    });
  });

  return (
    <div ref={setRootRef} className="cp-weekly-learning-guide-a4-term-stack">
      {entries.map((entry, index) => (
        <WeeklyGuideA4CardSheet
          key={`${entry.blockIndex}-${entry.pageIndex}`}
          block={entry.block}
          periodIndexes={entry.periodIndexes}
          showNotice={entry.pageIndex === entry.pageCount - 1}
          pageLabel={entry.pageCount > 1 ? `(${entry.pageIndex + 1}/${entry.pageCount})` : undefined}
          measuring={pagesByBlock === null}
          pageBreakAfter={index < entries.length - 1}
        />
      ))}
    </div>
  );
});

export default WeeklyLearningGuideA4CardDocument;
